import React, { ComponentType } from 'react';
import { ExtractProps } from 'app/utils/types';
import FluentdLogger from 'app/services/fluentdLogger';
import Logger from 'app/services/logger';
import { makeCachedLoader } from 'app/containers/state/makeCachedLoader';
import { LoadingStage } from 'app/containers/state/LoadingState';

const makeLazyLoadingWrapper = ({
  LoadingComponent,
  ErrorComponent,
}: {
  LoadingComponent: ComponentType,
  ErrorComponent: ComponentType<{ retryLoad: () => void }>,
}) => <T extends ComponentType<any>>(loadComponent: () => Promise<T>) => {
  const { useLoader } = makeCachedLoader(
    async (): Promise<T> => {
      try {
        return await loadComponent();
      } catch (err) {
        // Log the error to the console for local development purposes.
        Logger.error(err);

        // Send error to Fluentd so we can detect consistent errors.
        FluentdLogger.warn({ name: 'LazyLoading.load', message: err && err.message });

        throw err;
      }
    },
    {
      cacheTimeout: Infinity, // Never remove from cache
    },
  );

  return function LazyLoader(props: ExtractProps<T>) {
    const loadingState = useLoader({ args: undefined });

    switch (loadingState.stage) {
      case LoadingStage.Success: {
        const LoadedComponent = loadingState.value;
        return <LoadedComponent {...props} />;
      }

      case LoadingStage.Error: {
        return <ErrorComponent retryLoad={loadingState.reload} />;
      }

      case LoadingStage.Loading: {
        return <LoadingComponent />;
      }

      case LoadingStage.Idle: {
        throw new Error('Unexpected LoadingStage.Idle');
      }
    }
  };
};

export default makeLazyLoadingWrapper;
